import _ from 'lodash';
import { BlockEmbed } from 'quill/blots/block';
import Quill, { AttributeMap, Delta } from 'quill/core';
import React from 'react';
import { useAppDispatch } from '../../../app/redux/useAppSelector';
import { MenuItem, MenuSeparator } from '../../../components/Menu';
import { IMAGE_MIME_TYPES } from '../../../components/Uploader';
import addIcon from '../../../icons/non-prefilled/16x16/add.svg';
import { isFeatureEnabled } from '../../../utils/features';
import Sizes from '../../../utils/responsive/Sizes';
import { useIsSize } from '../../../utils/responsive/isSize';
import useIsPublicView from '../../../utils/useIsPublicView';
import type { HoveredBlotListenerSetting } from '../../HoveredBlotListener';
import { useHoveredBlotListener } from '../../HoveredBlotListener';
import { useQuill } from '../../QuillContext';
import ImageBlot, { compactCarouselItem } from '../../formats/ImageBlot';
import type { CarouselItem } from '../../image/Carousel';
import { areCarouselImages } from '../../image/Carousel';
import EditorModal from '../../modals/EditorModal';
import type { Blot } from '../../types';
import { expandIcon, imageLeftIcon, imageRightIcon } from '../editorIcons';
import DeleteMenuItem from './DeleteMenuItem';
import FloatingToolbar from './FloatingToolbar';
import Toolbar, { ToolbarOverflowMenu } from './Toolbar';
import ToolbarButton, { ToolbarButtonGroup } from './buttons/ToolbarButton';
import ToolbarCommentButtonGroup from './buttons/ToolbarCommentButtonGroup';
import ToolbarHeightButtonGroup from './buttons/ToolbarHeightButtonGroup';
import ToolbarSpanButtonGroup from './buttons/ToolbarSpanButtonGroup';
import useBlotHighlightedClass from './useBlotHighlightedClass';
import { UIActions } from '../../../ui/uiSlice';

type Formats = Record<string, string[] | string | null | undefined> & {
  comment?: string[] | null;
};

type UpdateImage = (change: {
  width?: number | null;
  attributes: AttributeMap;
}) => void;

interface ImageToolbarOverflowMenuProps {
  onViewFullscreen: () => void;
  blot: ImageBlot;
}

export const ImageToolbarOverflowMenu = ({
  blot,
  onViewFullscreen,
}: ImageToolbarOverflowMenuProps) => {
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  return (
    <>
      {isFeatureEnabled('image_carousel') && (
        <MenuItem
          id="aria-add-image-menu-item"
          onClick={() => fileInputRef.current?.click()}
          icon={addIcon}
        >
          <input
            ref={fileInputRef}
            type="file"
            hidden
            multiple
            accept={IMAGE_MIME_TYPES}
            onChange={async (e) => {
              if (e.target.files) {
                await blot.uploadFiles(Array.from(e.target.files));
              }
            }}
            aria-labelledby="aria-add-image-menu-item"
          />
          Add more images
        </MenuItem>
      )}
      <MenuItem icon={expandIcon} onClick={onViewFullscreen}>
        View expanded
      </MenuItem>
      <MenuSeparator />
      <DeleteMenuItem onClick={() => blot.remove()}>
        Delete {blot.isCarousel() ? 'carousel' : 'image'}
      </DeleteMenuItem>
    </>
  );
};

interface BaseImageToolbarProps {
  hoveredBlot: ImageBlot;
  formats: Formats;
  firstImageWidth?: number | null;
  updateImage: UpdateImage;
  setPersist?: (persist: boolean) => void;
}

export const BaseImageToolbar = ({
  hoveredBlot,
  formats,
  firstImageWidth,
  updateImage,
  setPersist,
}: BaseImageToolbarProps) => {
  const { readOnly } = useQuill();
  const dispatch = useAppDispatch();
  useBlotHighlightedClass(hoveredBlot);

  const isPublicView = useIsPublicView();
  const isCarousel = hoveredBlot.isCarousel();
  const isMobile = useIsSize(Sizes.SM);

  const viewFullscreen = () => {
    const { value, carouselIndex } = hoveredBlot.store.getState();
    if (Array.isArray(value)) {
      if (areCarouselImages(value)) {
        dispatch(
          UIActions.openModal(EditorModal, {
            carousel: {
              images: value,
              index: carouselIndex ?? 0,
            },
          }),
        );
      } else {
        const firstImage = value[0];
        if (!firstImage) return;
        dispatch(
          UIActions.openModal(EditorModal, {
            image: {
              src: firstImage.source,
              caption: firstImage.caption,
            },
          }),
        );
      }
    }
  };

  if (readOnly && isPublicView) {
    return (
      <Toolbar>
        <ToolbarButtonGroup>
          <ToolbarButton
            icon={expandIcon}
            tooltip="View&nbsp;expanded"
            onClick={viewFullscreen}
            target="_blank"
          >
            Expand
          </ToolbarButton>
        </ToolbarButtonGroup>
      </Toolbar>
    );
  }

  return (
    <Toolbar
      targetBlot={hoveredBlot}
      overflowMenu={
        readOnly ? undefined : (
          <ToolbarOverflowMenu
            onMenuOpenChange={setPersist}
            rightAlignOverflowMenu
          >
            <ImageToolbarOverflowMenu
              blot={hoveredBlot}
              onViewFullscreen={viewFullscreen}
            />
          </ToolbarOverflowMenu>
        )
      }
    >
      {readOnly && (
        <ToolbarButtonGroup>
          <ToolbarButton
            icon={expandIcon}
            tooltip="View expanded"
            onClick={viewFullscreen}
          >
            Expand
          </ToolbarButton>
        </ToolbarButtonGroup>
      )}
      {!readOnly && (
        <>
          {!(isCarousel && isMobile) && (
            <ToolbarSpanButtonGroup
              allowHalfWidth={!isCarousel}
              disallowDefaultWidthActive={
                firstImageWidth != null || !!formats.float
              }
              span={formats.span}
              onSpanChange={(newSpan) => {
                if (newSpan === 'half') {
                  const img = hoveredBlot.domNode.querySelector('img');
                  const naturalWidth = img?.naturalWidth;
                  updateImage({
                    width: naturalWidth ? naturalWidth / 2 : undefined,
                    attributes: { span: 'half', float: null },
                  });
                } else {
                  updateImage({
                    width: null,
                    attributes: { span: newSpan, float: null },
                  });
                }
              }}
            />
          )}
          {isCarousel && (
            <ToolbarHeightButtonGroup
              height={formats.height}
              onHeightChange={(newHeight) =>
                updateImage({
                  attributes: { height: newHeight },
                })
              }
            />
          )}
          {!isCarousel && (
            <ToolbarButtonGroup aria-label="Float options" role="radiogroup">
              <ToolbarButton
                accessibleType="radio"
                icon={imageLeftIcon}
                tooltip="Float left"
                active={formats.float === 'left'}
                onClick={() =>
                  updateImage({
                    attributes: { span: null, float: 'left' },
                  })
                }
              />
              <ToolbarButton
                accessibleType="radio"
                icon={imageRightIcon}
                tooltip="Float right"
                active={formats.float === 'right'}
                onClick={() =>
                  updateImage({
                    attributes: { span: null, float: 'right' },
                  })
                }
              />
            </ToolbarButtonGroup>
          )}
        </>
      )}
      <ToolbarCommentButtonGroup
        blot={hoveredBlot}
        existingComment={formats.comment}
        onReactionPickerOpenChange={setPersist}
      />
    </Toolbar>
  );
};

type Context = {
  firstImageWidth?: number;
  naturalWidth?: number;
  formats: Record<string, string[] | string | null | undefined>;
};

const hoveredBlotListenerSetting: HoveredBlotListenerSetting<Context> = {
  getHoveredBlot(blot: Blot, { node }) {
    return blot.statics.blotName === ImageBlot.blotName &&
      (ImageBlot.isImageOrImageContainerNode(node) ||
        ImageBlot.isWithinCarousel(node))
      ? blot
      : null;
  },
  getContext(blot: ImageBlot) {
    const value = blot.value().image;

    if (
      isFeatureEnabled('image_carousel') &&
      Array.isArray(value) &&
      value.length > 1
    ) {
      return {
        formats: blot.formats(),
      };
    }

    const firstImageWidth = Array.isArray(value) ? value[0]?.width : undefined;

    return {
      firstImageWidth,
      formats: blot.formats(),
    };
  },
};

const checkIsCarouselItems = (
  imageValue: unknown,
): imageValue is CarouselItem[] => Array.isArray(imageValue);

const ImageToolbar = () => {
  const { quill } = useQuill();

  const {
    bounds,
    hoveredBlot,
    setPersist,
    context: { firstImageWidth, formats } = {
      firstImageWidth: undefined,
      formats: undefined,
    },
  } = useHoveredBlotListener(quill, hoveredBlotListenerSetting);

  const updateImage: UpdateImage = (change) => {
    if (!(hoveredBlot instanceof BlockEmbed)) return;
    const delta = new Delta().retain(hoveredBlot.offset(hoveredBlot.scroll));
    let retain: 1 | { image: CarouselItem[] } = 1;
    const currentFormats = hoveredBlot.formats();
    const attributes = AttributeMap.diff(
      currentFormats,
      AttributeMap.compose(currentFormats, change.attributes, false),
    );

    if (typeof change.attributes.height !== 'undefined') {
      const value = hoveredBlot.value().image;
      if (checkIsCarouselItems(value)) {
        retain = 1;
      }
    } else if (typeof change.width !== 'undefined') {
      const value = hoveredBlot.value().image;
      if (checkIsCarouselItems(value)) {
        const firstImage = value[0];
        if (firstImage) {
          retain = {
            image: [
              compactCarouselItem({
                ...firstImage,
                width: change.width ?? undefined,
              }),
              ...value.slice(1),
            ],
          };
        }
      }
    }

    if (quill && (retain !== 1 || !_.isEmpty(attributes))) {
      quill.updateContents(
        delta.retain(retain, attributes),
        Quill.sources.USER,
      );
    }
  };

  return (
    <FloatingToolbar leftAligned={formats?.float === 'left'} bounds={bounds}>
      {hoveredBlot instanceof ImageBlot && formats && (
        <BaseImageToolbar
          hoveredBlot={hoveredBlot}
          formats={formats}
          firstImageWidth={firstImageWidth}
          updateImage={updateImage}
          setPersist={setPersist}
        />
      )}
    </FloatingToolbar>
  );
};

export default ImageToolbar;
